import { fetchJson } from "../../../helpers/fetch.js";
import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useProjectResourceFormOptions } from "./useProjectResourceFormOptions.js";
import { buildColumnDefs } from "../../datagrid/hooks/useDynamicCols.jsx";
import queryClient from "../../../../query_client.js";
import { FaIcon } from "../../common/Icons.jsx";
import UserSelectCellEditor from "../../datagrid/UserSelectCellEditor.jsx";
import CustomMuiTooltip from "../../common/CustomMuiTooltip.jsx";

import {
  project_resource_update_cell_path,
  project_resource_lock_cell_path,
  project_resource_accept_cell_path,
  common_user_organisation_tree_options_path
} from '~/routes';
import { useMonths } from "./useMonths.js";
import { useQuery } from "@tanstack/react-query";

export const stripEmail = (label) => {
  if (typeof label !== 'string') return label;
  const i = label.lastIndexOf(' (');
  return i !== -1 && label.endsWith(')') ? label.slice(0, i) : label;
};

const lockedCellRenderer = (params, canEdit, lockedKey, value = null, setDialogProps) => {
  const val = value || params.value;
  const isLocked = params.data?.[lockedKey];
  const programmeTotal = params.data?.[`${params.colDef.field}_programme`];

  const onAllocationsClick = () => {
    const { colId: month } = params.column;
    const { person_id, person_name } = params.data;
    setDialogProps?.({ person_id, person_name, month });
  }

  let suffix = programmeTotal && val !== programmeTotal
    ?
      <CustomMuiTooltip
        placement='top'
        content={<>
          {I18n.t('person_total_utilization_across_projects')}
          <div>
            <a style={{cursor: 'pointer'}} onClick={onAllocationsClick}>
            {I18n.t('click_to_view_details')}
          </a>
          </div>
        </>}
        hideTimeout={200}
      >
        <span style={{marginLeft: 5, color: '#777'}} onClick={onAllocationsClick}>
          [{programmeTotal}]
        </span>
      </CustomMuiTooltip>
        
    : null

  const isPersonField = params.colDef.field?.startsWith('person');
  const isUserTypeField = params.colDef.field?.startsWith('user_type');

  if ((isPersonField || isUserTypeField) && params.data?.expired && (!canEdit || !isUserTypeField)) {
    suffix = <span style={{marginLeft: 5}}>({I18n.t('expired').toLowerCase()})</span>
  }

  if (canEdit && isLocked) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <FaIcon type='lock' className='text-muted' style={{ fontSize: '0.8em', marginTop: 2 }} />
        <span>
          {val} {suffix}
        </span>
      </span>
    );
  };

  return <>{val}{suffix}</>;
}

export const useProjectResourceGrid = ({ projectId, programmeId, groupBy, months: initialMonths, permissions, editMode, viewMode, personsFilter = false }) => {
  const { user_types } = useProjectResourceFormOptions({projectId});
  const [targetProjectId, setTargetProjectId] = useState(null);
  const [markExpiredResourceId, setMarkExpiredResourceId] = useState(null);
  const [replacePersonResourceId, setReplacePersonResourceId] = useState(null);
  const [dialogProps, setDialogProps] = useState(null);

  const { data: organizationOptions = [], isLoading: isLoadingPersonIds } = useQuery({
    queryKey: ['commonUserOrganisationTreeOptions'],
    queryFn: () => fetchJson(common_user_organisation_tree_options_path()),
    staleTime: 5 * 60 * 1000,
    enabled: !!personsFilter
  });

  const allPersonIds = useMemo(() => {
    return organizationOptions.filter((item) => item.is_user).map((item) => item.id.replace('-user', ''));
  }, [organizationOptions]);
  
  const { months } = useMonths({projectId, months: initialMonths});
  const editModeRef = useRef(editMode);
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  const overrideProps = viewMode ? {
    minWidth: undefined,
    pinned: undefined
  } : {}

  const monthColumns = useMemo(() => {
    if (!months) return null;
    return months.map(m => ({
      name: m.label,
      accessor: m.field,
      column_type: 'decimal',
      columnProps: {
        headerClass: 'ag-right-aligned-header',
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        enableRowGroup: false,
        editable: params => {
          const lockedKey = `${m.field}_locked`
          return editModeRef.current && !!permissions.update_cell && !params.data?.[lockedKey]
        },
        sortable: false,
        filter: false,
        minWidth: 120,
        valueParser: ({newValue}) => newValue && Number(newValue),
        cellRenderer: params =>  lockedCellRenderer(params, editModeRef.current && !!permissions.update_cell, `${m.field}_locked`, null, setDialogProps),
        headerStyle: { borderRight: '1px solid #D9D9D9' },
        cellStyle: params => {
          const focused = params.api.getFocusedCell();
          const acceptedKey = `${params.column.colId}_accepted`;
          const monthAccepted = params.data[acceptedKey];

          if (
            focused &&
            focused.rowIndex === params.rowIndex &&
            params.column.getId() === focused.column.getId()
          ) {
            return {
              backgroundColor: monthAccepted ? 'rgba(32, 191, 106, 0.2)' : undefined

            };
          }
          return { 
            backgroundColor: monthAccepted ? 'rgba(32, 191, 106, 0.2)' : undefined,
            borderRight: '1px solid #D9D9D9' 
          };
        },
        ...overrideProps
      }
    }))
  }, [months]);

  const columnDefs = useMemo(() => {
    if (!monthColumns) return null;
    const initialColumnDefs = [
      {
        name: I18n.t('person'),
        accessor: 'person_name',
        column_type: 'text',
        is_selection: true,
        selection_options: [],
        columnProps: {
          enableRowGroup: false,
          filter: 'agTextColumnFilter',
          hide: groupBy === 'person',
          editable: params =>  editModeRef.current && !!permissions.update_cell && !params.data.person_locked,
          minWidth: 180,
          pinned: 'left',
          cellEditor:  UserSelectCellEditor,
          cellRenderer: params => {
            const val = params.value?.value || params.value;
            return lockedCellRenderer(params, editModeRef.current && !!permissions.update_cell, 'person_locked', val);
          },
          ...overrideProps
        }
      },
      {
        accessor: 'group_person_id',
        columnProps: {
          hide: true,
          rowGroup: groupBy === 'person',
          enableRowGroup: false
        }
      },
      {
        accessor: 'person_id',
        columnProps: {
          hide: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: allPersonIds,
          },
        },
      },
      {
        name: I18n.t('role'),
        column_type: 'serial_number',
        accessor: 'user_type',
        is_selection: true,
        exclude_blank_selection: true,
        selection_options: user_types || [],
        columnProps: {
          enableRowGroup: false,
          rowGroup: groupBy === 'role',
          hide: groupBy === 'role',
          pinned: 'left',
          editable: params => editModeRef.current && !!permissions.update_cell && !params.data.user_type_locked,
          cellRenderer: params => {
            const val = params.data?.['user_type']?.['label'] || params.valueFormatted
            return lockedCellRenderer(params, editModeRef.current && !!permissions.update_cell, 'user_type_locked', val);
          },
          minWidth: 180,
          ...overrideProps
        }
      },
      {
        name: I18n.t('additional_information'),
        column_type: 'text',
        accessor: 'additional_information',
        columnProps: {
          enableRowGroup: false,
          pinned: 'left',
          editable: () => editModeRef.current && !!permissions.update_cell,
          minWidth: 180,
          ...overrideProps
        }
      },
      ...(programmeId === projectId ?
        [{
            name: I18n.t('project'),
            column_type: 'project',
            accessor: 'project',
            columnProps: {
              hide: groupBy === 'project',
              rowGroup: groupBy === 'project',
              enableRowGroup: false,
              pinned: 'left',
              minWidth: 180,
              ...overrideProps
            }
          }] : []
        ),
      ...monthColumns,
      {
        name: I18n.t('total_sum'),
        accessor: 'total_hours',
        column_type: 'decimal',
        columnProps: {
          headerClass: 'ag-right-aligned-header',
          filter: false,
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          enableRowGroup: false,
          editable: params => editModeRef.current && !!permissions.update_cell && !params.data.total_locked,
          valueGetter: params => params.data['total_hours'] && Number(params.data['total_hours']),
          valueParser: ({newValue}) => newValue && Number(newValue),
          cellRenderer: params => lockedCellRenderer(params, editModeRef.current && !!permissions.update_cell, 'total_locked', null, setDialogProps),
          minWidth: 120,
          pinned: 'right',
          ...overrideProps
        }
      }
    ]
    return buildColumnDefs(initialColumnDefs);
  }, [groupBy, monthColumns, overrideProps, allPersonIds]);

  const getMainMenuItems = useCallback((params) => {
    return ['sortAscending', 'sortDescending', 'separator', 'autoSizeThis', 'autoSizeAll'];
  }, []);

  const getContextMenuItems = useCallback((params) => {
    if (viewMode) return [];
    const field = params.column.colId;
    const trimmedField = field.replace(/_(name|hours)$/, '');
    const lockedKey = `${trimmedField}_locked`;
    const acceptedKey = `${trimmedField}_accepted`;
    const isLocked = params.node.data?.[lockedKey] || false;
    const isAccepted = params.node.data?.[acceptedKey] || false;
    const isEditMode = editModeRef.current;

    const expired = params.node.data?.expired || false;
    const canExpire = !expired && isEditMode && permissions.mark_as_expired &&
      (trimmedField.startsWith('person') || trimmedField.startsWith('user_type'));
    const canAccept = !expired && isEditMode && !params.node_group && permissions.accept_cell && acceptedKey.startsWith('m_');
    const canLock = !expired && isEditMode && !params.node.group && permissions.lock_cell && !isAccepted &&
      !lockedKey.startsWith('additional_information') && !lockedKey.startsWith('project');

    var contextMenuItems = [];
      contextMenuItems = contextMenuItems.concat(
      [
        ...(canExpire ? [{
          name: I18n.t('mark_as_expired'),
          action: () => {
            setMarkExpiredResourceId(params.node.data.project_resource_id);
            setTargetProjectId(params.node.data.project_id);
          },
          icon: `<i class="fa fa-user-slash" style="margin-left: 2px;"></i>`
        }] : []),
        ...(canExpire ? [{
          name: I18n.t('replace_person_and_expire'),
          action: () => {
            setReplacePersonResourceId(params.node.data.project_resource_id);
            setTargetProjectId(params.node.data.project_id);
          },
          icon: `<i class="fa fa-right-left" style="margin-left: 2px;"></i>`
        }] : []),
        ...(canAccept ? [{
          name: I18n.t(isAccepted ? 'mark_as_not_accepted_by_supervisor' : 'mark_as_accepted_by_supervisor'),
          action: async () => {
            await setCellAccepted({
              value: !isAccepted,
              oldValue: isAccepted,
              field: acceptedKey,
              data: params.node.data,
              node: params.node,
              api: params.api
            })
          },
          icon: `<i class="fa fa-${isAccepted ? 'x' : 'check'}" style="margin-left: 2px;"></i>`
        }] : []),
        ...(canLock ? [{
          name: I18n.t(isLocked ? 'unlock_cell' : 'lock_cell'),
          action: async () => {
            await setCellLocked({
              value: !isLocked,
              oldValue: isLocked,
              field: lockedKey,
              data: params.node.data,
              node: params.node,
              api: params.api
            })
          },
          icon: '<i class="fa fa-lock" style="margin-left: 2px;"></i>'
        }] : []),
        'cut',
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'paste'
      ]
    );

    return contextMenuItems;
  }, [editMode]);

  const setCellAccepted = async ({value, oldValue, field, data, node, api}) => {
    const payload = {
      cell: {
        project_resource_id: data.project_resource_id,
        field,
        value,
      },
    };

    try {
      const res = await fetchJson(
        project_resource_accept_cell_path({ project_id: data.project_id }),
        { method: 'POST', body: JSON.stringify(payload) }
      );

      queryClient.invalidateQueries(['projectResourceList']);

      if (res?.errors) {
        node.setDataValue(field, oldValue);
        return;
      }

      if (res?.rows) {
        node.setData(res.rows[0]);
        return;
      }

      const val = res?.value ?? res;
      node.setDataValue(field, val);
      api.refreshServerSide?.();
    } catch (e) {
      node.setDataValue(field, oldValue);
    }
  }

  const setCellLocked = async ({value, oldValue, field, data, node, api}) => {
    const payload = {
      cell: {
        project_resource_id: data.project_resource_id,
        field,
        value,
      },
    };

    try {
      const res = await fetchJson(
        project_resource_lock_cell_path({ project_id: data.project_id }),
        { method: 'POST', body: JSON.stringify(payload) }
      );

      queryClient.invalidateQueries(['projectResourceList']);

      if (res?.errors) {
        node.setDataValue(field, oldValue);
        return;
      }

      if (res?.rows) {
        node.setData(res.rows[0]);
        return;
      }

      const val = res?.value ?? res;
      node.setDataValue(field, val);
      api.refreshServerSide?.();
    } catch (e) {
      node.setDataValue(field, oldValue);
    }
  }

  const onCellEditingStopped = async ({ value: initialValue, oldValue, colDef, data, node }) => {
    const field = colDef.field;

    const isUserOption = !!initialValue?.value && initialValue?.is_user;
    const displayValue = isUserOption ? stripEmail(initialValue?.label) : (initialValue?.value ?? initialValue);
    const valueToSend = initialValue?.value ?? initialValue;

    if (valueToSend === oldValue) return;

    if (isUserOption) node.setDataValue(field, displayValue);

    const payload = {
      cell: {
        project_resource_id: data.project_resource_id,
        field,
        value: valueToSend,
      },
    };

    try {
      const res = await fetchJson(
        project_resource_update_cell_path({ project_id: data.project_id }),
        { method: 'POST', body: JSON.stringify(payload) }
      );

      queryClient.invalidateQueries(['projectResourceList']);

      if (res?.errors) {
        node.setDataValue(field, oldValue);
        return;
      }

      if (res?.rows) {
        node.setData(res.rows[0]);
        return;
      }

      const val = res?.value ?? res;
      node.setDataValue(field, isNaN(Number(val)) ? val : Number(val));
    } catch (e) {
      node.setDataValue(field, oldValue);
    }
  };

  const closeModals = () => {
    setMarkExpiredResourceId(null);
    setReplacePersonResourceId(null);
    setTargetProjectId(null);
    setDialogProps(null);
  }

  return {
    months,
    columnDefs,
    onCellEditingStopped,
    getContextMenuItems,
    getMainMenuItems,
    markExpiredResourceId,
    replacePersonResourceId,
    closeModals,
    targetProjectId,
    dialogProps
  };
}